import {
  BOW_OFFSET_PX,
  INITIAL_HEADING,
  INITIAL_POSITION,
  INITIAL_SPEED,
  MIN_SPEED,
  MOVEMENT_RADIUS,
  TURN_TAX,
  TURN_TAX_MAX,
  TURN_TAX_MIN,
} from './constants.ts';
import {
  add,
  angleDelta,
  clamp,
  dot,
  fromAngle,
  headingUnit,
  length,
  lengthSq,
  sub,
  toAngle,
} from './math.ts';
import { pointInReach, type ReachAxes } from './boat/reach.ts';
import { nextDrive } from './crashCost.ts';
import type { BoatState, MovementArea, Vec2 } from './types.ts';

/** Below this the turn is treated as straight, avoiding a 0/0 arc length. */
const STRAIGHT_OFFSET = 1e-9;

export function createBoatState(
  overrides: {
    position?: Partial<Vec2>;
    heading?: number;
    speed?: number;
    drive?: 0 | 1 | 2;
  } = {},
): BoatState {
  const speed = overrides.speed ?? INITIAL_SPEED;

  return {
    position: {
      x: overrides.position?.x ?? INITIAL_POSITION.x,
      y: overrides.position?.y ?? INITIAL_POSITION.y,
    },
    heading: overrides.heading ?? INITIAL_HEADING,
    speed,
    drive: overrides.drive ?? (speed > 0 ? 2 : 0),
  };
}

const NO_WIND: Vec2 = { x: 0, y: 0 };

export function projectMovementArea(
  boat: BoatState,
  radius: number = MOVEMENT_RADIUS,
  windOffset: Vec2 = NO_WIND,
  /** Pixels of coast this turn. Omitted, the centre sits on the full velocity. */
  coastPx: number = boat.speed,
): MovementArea {
  return {
    center: add(add(boat.position, fromAngle(boat.heading, coastPx)), windOffset),
    radius,
  };
}

export function bowPosition(
  boat: Pick<BoatState, 'position' | 'heading'>,
  bowOffsetPx: number = BOW_OFFSET_PX,
): Vec2 {
  return add(boat.position, fromAngle(boat.heading, bowOffsetPx));
}

export function isValidDestination(
  boat: BoatState,
  point: Vec2,
  area: MovementArea = projectMovementArea(boat),
  /**
   * Heading-aligned oval around `area.center`. Omitted, races keep the disk.
   * Equal turn / accel / brake match that disk.
   */
  reach: ReachAxes | null = null,
  bowOffsetPx: number = BOW_OFFSET_PX,
): boolean {
  if (reach) {
    if (!pointInReach(area.center, boat.heading, point, reach)) {
      return false;
    }
  } else {
    const offsetFromCenter = sub(point, area.center);

    if (lengthSq(offsetFromCenter) > area.radius * area.radius) {
      return false;
    }
  }

  return dot(sub(point, bowPosition(boat, bowOffsetPx)), headingUnit(boat.heading)) > 0;
}

/**
 * The constant-rate turn that carries a boat from its current position and
 * heading to a destination.
 */
export type TurnArc = {
  /** Signed angle between the entry heading and the chord. Zero when straight. */
  offset: number;
  /**
   * Heading the boat exits on. By the tangent-chord angle theorem this
   * overshoots the chord by `offset`, because the chord only describes the
   * average direction of travel, not the final one.
   */
  heading: number;
  /** Arc length actually swum, never shorter than the chord. Drawing only. */
  travelled: number;
  /**
   * Momentum kept for the next center. The chord at tax 0; a tightness
   * penalty lerps toward `chord × sinc(offset)` as tax goes to 1.
   */
  speed: number;
};

/**
 * Kept speed for a click of `chord` metres at signed heading `offset`.
 * Tax 0 is the chord; tax 1 is `chord × sin(offset) / offset`.
 */
export function turnKeptSpeed(
  chord: number,
  offset: number,
  tax: number = TURN_TAX,
): number {
  const amount = clamp(tax, TURN_TAX_MIN, TURN_TAX_MAX);

  if (Math.abs(offset) < STRAIGHT_OFFSET || amount === 0) {
    return chord;
  }

  const keep = (chord * Math.sin(offset)) / offset;

  return chord + (keep - chord) * amount;
}

export function solveTurnArc(
  boat: BoatState,
  destination: Vec2,
  tax: number = TURN_TAX,
): TurnArc | null {
  const chord = sub(destination, boat.position);
  const distance = length(chord);

  if (distance === 0) {
    return null;
  }

  const chordHeading = toAngle(chord);

  // Heading is the entry tangent even from rest, so the first move is the
  // same turn as later ones. Dead-ahead stays straight (offset ≈ 0).
  const offset = angleDelta(boat.heading, chordHeading);

  if (Math.abs(offset) < STRAIGHT_OFFSET) {
    return {
      offset: 0,
      heading: chordHeading,
      travelled: distance,
      speed: distance,
    };
  }

  return {
    offset,
    heading: angleDelta(0, chordHeading + offset),
    travelled: (distance * offset) / Math.sin(offset),
    speed: turnKeptSpeed(distance, offset, tax),
  };
}

/**
 * Heading change of the turn in degrees, or `null` when the move is straight.
 */
export function turnAngleDeg(
  boat: BoatState,
  destination: Vec2,
): number | null {
  const arc = solveTurnArc(boat, destination);

  if (!arc || Math.abs(arc.offset) < STRAIGHT_OFFSET) {
    return null;
  }

  return (Math.abs(arc.offset) * 180) / Math.PI;
}

export function resolveMove(
  boat: BoatState,
  destination: Vec2,
  tax: number = TURN_TAX,
  /** Replaces the chord when a caller has already decided a kept speed. */
  keptSpeed?: number,
): BoatState {
  const arc = solveTurnArc(boat, destination, tax);

  if (!arc) {
  return {
    position: { x: destination.x, y: destination.y },
    heading: boat.heading,
    speed: 0,
    drive: nextDrive(boat),
  };
}

  return {
    position: { x: destination.x, y: destination.y },
    heading: arc.heading,
    speed: Math.max(MIN_SPEED, keptSpeed ?? arc.speed),
    drive: nextDrive(boat),
  };
}
